import { ReactNode } from "react";

import { SanityGlobalRegion, SanitySubject } from "../../model/common";
import {
  SelectionMutator,
  SelectionPredicate
} from "./useFilterSelectionState";

export interface ListingConfig<T> {
  filters: FilterConfigItem<T>[];
  searchFields: (v: T) => string[];
}

export interface FilterConfigItem<T> {
  id: string;
  titleId: string;
  location: "top" | "side";
  values: (item: T) => FilterType[];
  filter: (selectedValues: FilterType[], data: T[]) => T[];
  component: (props: {
    titleId: string;
    filterData: FilterType[];
    isSelected: SelectionPredicate;
    setSelected: SelectionMutator;
    inputNamePrefix: string;
    filterCounts: FilterCounts;
  }) => ReactNode;
}

export interface FilterType {
  _id: string;
  _type?: string;
  name: string;
  subject?: SanitySubject;
  globalRegion?: SanityGlobalRegion;
}

export interface FilterCounts {
  [id: string]: number;
}

export interface FiltersDetails {
  ageRanges: FilterType[];
  programmingLanguages: FilterType[];
  topics: FilterType[];
  subjects: FilterType[];
}

export const applyFilters = <T extends object>(
  config: ListingConfig<any>,
  filtersByConfigId: { [id: string]: FilterType[] },
  isSelected: SelectionPredicate,
  data: T[]
) => {
  let result = data;
  for (const filter of config.filters) {
    const filters = filtersByConfigId[filter.id];
    if (filters) {
      const selectedFilters = filters.filter(isSelected);
      if (selectedFilters.length) {
        result = filter.filter(selectedFilters, result);
      }
    }
  }
  return result;
};

export const applyQuery = <T>(
  searchFields: (v: T) => (string | undefined)[],
  query: string,
  data: T[]
): T[] => {
  const queryWords = query
    .toLocaleLowerCase()
    .split(/\s+/)
    .map(x => x.trim());
  return data.filter(x =>
    queryWords.every(
      w =>
        !!searchFields(x).find(
          f => f && f.toLocaleLowerCase().indexOf(w) !== -1
        )
    )
  );
};

export const calculateFilterCounts = <T extends object>(
  config: ListingConfig<any>,
  contents: T[]
): FilterCounts => {
  const result: FilterCounts = {};
  contents.forEach(item => {
    const valuesById: { [id: string]: FilterType } = {};
    config.filters.forEach(c =>
      c.values(item).forEach(v => (valuesById[v._id] = v))
    );
    Object.values(valuesById).forEach(
      v => (result[v._id] = (result[v._id] || 0) + 1)
    );
  });
  return result;
};
